import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {VCT_COLORS, VCT_NEGATIVE_COLOR} from '../../../../../constants/charts';
import {VctCollectedChartData} from '../../../../models/vct-collected-chart-data.interface';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

@Component({
  selector: 'app-vct-up-to-bar-chart',
  templateUrl: './vct-up-to-bar-chart.component.html',
  styleUrls: ['./vct-up-to-bar-chart.component.scss']
})
export class VctUpToBarChartComponent implements OnInit, OnChanges {

  @Input()
  data!: VctCollectedChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes!: string[];
  textBelowCode = 'kfcharts.Country.Collected.CollectionTargetUpTo2018.textBelow';
  isAdmin!: boolean;

  private static getCountryCodes(chartData: VctCollectedChartData[]): string[] {
    const chartCodes = chartData?.map(item => item.countryCode);
    return [...new Set(chartCodes)];
  }

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.countryCodes = VctUpToBarChartComponent.getCountryCodes(this.data);

      // highcharts
      this.chartData = this.convertToChartData(this.data);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: VCT_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              format: '{point.y:.2f}'
            },
          },
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [pointOptions.custom?.countryCode];
              }
              return ['<div class="center"><b>' + pointOptions?.custom?.countryName + '</b></div><br><br>',
                '45% of average market input (' + (pointOptions?.custom?.startYear) + ' - ' + (pointOptions?.custom?.endYear) + '): '
                + formatNumber(pointOptions?.custom?.vctAverage) + ' kg/inh.'];
            }
            return undefined;
          }
        },
        xAxis: {
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(data: VctCollectedChartData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    chartData.push({
      type: 'column',
      name: '45% of average market input',
      negativeColor: VCT_NEGATIVE_COLOR,
      data: data.map(item => {
        return {
          y: Number(item.value.toFixed(2)),
          name: item.countryName,
          custom: {
            countryCode: item.countryCode,
            countryName: item.countryName,
            vctAverage: item.value,
            startYear: item.startYear,
            endYear: item.endYear
          }
        };
      })
    });

    return chartData;
  }


}
